import type {LucideIcon} from 'lucide-react'
import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type InfoBannerVariant = 'info' | 'success' | 'muted' | 'destructive' | 'warning'

interface InfoBannerProps {
    children: ReactNode
    variant?: InfoBannerVariant
    icon?: LucideIcon
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const variantClasses: Record<InfoBannerVariant, string> = {
    info: 'rounded-md border border-blue-200 bg-status-info-subtle dark:border-blue-900 dark:bg-status-info-subtle/30 px-3 py-2 text-sm text-status-info dark:text-status-info-muted',
    success: 'rounded-lg bg-status-success-subtle px-4 py-2 text-sm text-status-success',
    muted: 'rounded-lg bg-muted p-4 text-sm',
    destructive: 'rounded-lg bg-destructive/10 px-4 py-2 text-sm text-destructive',
    warning: 'rounded-md border border-amber-200 bg-status-warning-subtle dark:border-amber-800 dark:bg-status-warning-subtle p-3 text-sm text-status-warning dark:text-status-warning-muted',
}
/* eslint-enable i18next/no-literal-string */

export function InfoBanner({children, variant = 'muted', icon: IconComponent, className, 'data-testid': testId}: InfoBannerProps) {
    if (IconComponent) {
        return (
            <div className={cn('flex items-center gap-2', variantClasses[variant], className)} data-testid={testId}>
                <IconComponent className="h-4 w-4 shrink-0"/>
                <div>{children}</div>
            </div>
        )
    }

    return (
        <div className={cn(variantClasses[variant], className)} data-testid={testId}>
            {children}
        </div>
    )
}
